import type { ColumnDef } from "@tanstack/react-table";
import type { EntityKey } from "@/routes/paths";
import type { AnyEntityConfig } from "./types";

import { kycVerificationConfig, userConfig, walletConfig } from "./configs/identity";
import {
  holdConfig,
  ledgerEntryColumns,
  ownerKeyBackupConfig,
  ownerOperationConfig,
  paymentConfig,
  sessionKeyConfig,
  smartAccountOperationConfig,
  walletMigrationConfig,
  walletTransactionColumns,
} from "./configs/wallet";
import {
  reserveTopUpLegConfig,
  settlementConfig,
  settlementObligationConfig,
} from "./configs/settlement";
import { cardConfig, cardNotificationColumns } from "./configs/cards";
import { cardRefundConfig } from "./configs/cardRefunds";
import {
  depositConfig,
  forwarderSweepConfig,
  offrampBurnConfig,
  paymentAccountConfig,
  virtualAccountConfig,
  withdrawalConfig,
} from "./configs/ramps";
import {
  crossChainDepositConfig,
  crossChainSendConfig,
  depositAddressConfig,
  stablecoinReceiveAddressConfig,
} from "./configs/crosschain";
import {
  earnOperationConfig,
  earnPositionConfig,
  vaultApprovalConfig,
  vaultConfig,
} from "./configs/earn";

/** Entities that have a full list + detail screen. */
export const REGISTRY: Partial<Record<EntityKey, AnyEntityConfig>> = {
  user: userConfig,
  wallet: walletConfig,
  kycVerification: kycVerificationConfig,
  payment: paymentConfig,
  cardRefund: cardRefundConfig,
  sessionKey: sessionKeyConfig,
  ownerOperation: ownerOperationConfig,
  hold: holdConfig,
  walletMigration: walletMigrationConfig,
  ownerKeyBackup: ownerKeyBackupConfig,
  smartAccountOperation: smartAccountOperationConfig,
  settlementObligation: settlementObligationConfig,
  settlement: settlementConfig,
  reserveTopUpLeg: reserveTopUpLegConfig,
  card: cardConfig,
  deposit: depositConfig,
  withdrawal: withdrawalConfig,
  virtualAccount: virtualAccountConfig,
  paymentAccount: paymentAccountConfig,
  forwarderSweep: forwarderSweepConfig,
  offrampBurn: offrampBurnConfig,
  depositAddress: depositAddressConfig,
  stablecoinReceiveAddress: stablecoinReceiveAddressConfig,
  crossChainDeposit: crossChainDepositConfig,
  crossChainSend: crossChainSendConfig,
  earnPosition: earnPositionConfig,
  earnOperation: earnOperationConfig,
  vaultApproval: vaultApprovalConfig,
  vault: vaultConfig,
};

/** Columns for list-only entities (no dedicated detail screen). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LIST_ONLY_COLUMNS: Partial<Record<EntityKey, ColumnDef<any, unknown>[]>> = {
  ledgerEntry: ledgerEntryColumns,
  walletTransaction: walletTransactionColumns,
  cardNotification: cardNotificationColumns,
};

export function entityConfig(key: EntityKey): AnyEntityConfig | undefined {
  return REGISTRY[key];
}

/** Columns to render for an entity in a related/list table. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function columnsFor(key: EntityKey): ColumnDef<any, unknown>[] {
  return REGISTRY[key]?.columns ?? LIST_ONLY_COLUMNS[key] ?? [];
}

/** All full entity configs, for building list/detail routes. */
export const ALL_CONFIGS: AnyEntityConfig[] = Object.values(REGISTRY).filter(Boolean) as AnyEntityConfig[];
